import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { Calendar, TrendingUp, Users, ExternalLink, Clock, Shield, Award, Zap } from 'lucide-react';

const News = () => {
  const newsItems = [
    {
      id: 'winamax-expansion',
      title: "Winamax Expands Premium Sports Betting Portfolio for UK Players",
      excerpt: "France's leading sports betting platform introduces enhanced markets and improved odds for Premier League and Champions League matches, giving UK bettors access to world-class betting opportunities.",
      date: "14.12.2025",
      category: "Platform Updates",
      readTime: "3 min read",
      image: "/images/aceofnet-gTp5dGK6jtY-unsplash.webp",
      featured: true
    },
    {
      id: 'zeturf-jackpots',
      title: "Zeturf's Holiday Racing Festival Promises Record Jackpots",
      excerpt: "The French racing specialist announces unprecedented prize pools for the holiday season, with €50,000+ guaranteed jackpots across selected races and enhanced betting opportunities for international players.",
      date: "12.12.2025",
      category: "Promotions",
      readTime: "2 min read",
      image: "/images/carl-raw-dmc4sVdnSDs-unsplash.webp",
      featured: false
    },
    {
      id: 'bwin-live-casino',
      title: "Bwin Launches Revolutionary Live Casino Experience",
      excerpt: "Global gaming powerhouse unveils cutting-edge live dealer studios with professional UK croupiers, bringing authentic casino atmosphere to online players with crystal-clear streaming and interactive features.",
      date: "10.12.2025",
      category: "Innovation",
      readTime: "4 min read",
      image: "/images/eyestetix-studio--kTQitsL0Is-unsplash.webp",
      featured: false
    },
    {
      id: 'ukgc-2025-updates',
      title: "UK Gambling Commission Updates 2025 Licensing Requirements",
      excerpt: "Comprehensive regulatory overhaul introduces enhanced player protection measures and stricter financial controls for online operators serving UK markets.",
      date: "08.12.2025",
      category: "Regulation",
      readTime: "5 min read",
      image: "/images/jaroslaw-kwoczala-jLssTd4ngFA-unsplash.webp",
      featured: false
    },
    {
      id: 'crypto-casinos-uk',
      title: "Cryptocurrency Integration Transforms UK Online Casinos",
      excerpt: "Blockchain technology enables instant, secure transactions while regulatory frameworks adapt to accommodate digital currencies in mainstream gaming.",
      date: "06.12.2025",
      category: "Technology",
      readTime: "4 min read",
      image: "/images/marco-ribbe-DZ2FvK0eLjQ-unsplash.webp",
      featured: false
    },
    {
      id: 'mobile-gaming-2025',
      title: "Mobile Gaming Revolution: Next-Generation Technologies for 2025",
      excerpt: "Advanced mobile platforms deliver console-quality gaming experiences with seamless cross-device synchronization and innovative touch controls.",
      date: "04.12.2025",
      category: "Technology",
      readTime: "3 min read",
      image: "/images/ben-frost-vmJgtCmgmWI-unsplash.webp",
      featured: false
    },
    {
      id: 'responsible-gambling-2025',
      title: "Responsible Gambling Initiatives Reach New Heights in UK",
      excerpt: "Industry-leading operators implement AI-powered monitoring systems and personalized intervention strategies to enhance player protection.",
      date: "02.12.2025",
      category: "Responsible Gaming",
      readTime: "4 min read",
      image: "/images/nathana-reboucas-1riTdO9dW3c-unsplash.webp",
      featured: false
    },
    {
      id: 'cross-platform-gaming',
      title: "Cross-Platform Casino Gaming: Seamless Integration Becomes Reality",
      excerpt: "Unified gaming ecosystems allow players to transition seamlessly between desktop, mobile, and emerging platforms without losing progress or funds.",
      date: "30.11.2025",
      category: "Technology",
      readTime: "3 min read",
      image: "/images/ryan-grewell-hMONKLkuzpI-unsplash.webp",
      featured: false
    },
    {
      id: 'vip-programs-2025',
      title: "VIP Programs Redefined: Enhanced Loyalty Benefits for Elite Players",
      excerpt: "Premium operators introduce personalized experiences, exclusive events, and enhanced rewards that go beyond traditional loyalty structures.",
      date: "28.11.2025",
      category: "VIP Services",
      readTime: "4 min read",
      image: "/images/eyestetix-studio-bzYKqRsiINo-unsplash.webp",
      featured: false
    }
  ];

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Platform Updates': return 'bg-blue-100 text-blue-800';
      case 'Promotions': return 'bg-green-100 text-green-800';
      case 'Innovation': return 'bg-purple-100 text-purple-800';
      case 'Regulation': return 'bg-red-100 text-red-800';
      case 'Technology': return 'bg-indigo-100 text-indigo-800';
      case 'Responsible Gaming': return 'bg-teal-100 text-teal-800';
      case 'VIP Services': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-book-cream">
      
      <main id="news" className="book-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
              Latest Gaming Insights
            </h1>
            <p className="text-xl text-book-brown-dark max-w-4xl mx-auto leading-relaxed">
              Stay ahead of the curve with exclusive news, analysis, and expert commentary from the world of premium online gaming.
              We bring you the stories that matter to discerning players seeking competitive advantages in the UK casino landscape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {newsItems.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                data-discover="true"
                className="group bg-white rounded-xl shadow-book hover:shadow-lg transition-all duration-200 overflow-hidden"
              >
                <div className="aspect-video bg-gradient-to-br from-book-brown to-book-brown-light flex items-center justify-center">
                  <div className="text-center text-book-cream">
                    <TrendingUp className="h-10 w-10 mx-auto mb-2 opacity-80" />
                    <p className="text-sm font-medium">{item.imagePlaceholder}</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(item.category)}`}>
                      {item.category}
                    </span>
                    <div className="flex items-center space-x-1 text-book-brown-light">
                      <Calendar className="h-4 w-4" />
                      <span className="text-sm">{item.date}</span>
                    </div>
                    <div className="flex items-center space-x-1 text-book-brown-light">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">{item.readTime}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-book-brown mb-3 leading-tight group-hover:text-book-brown-light transition-colors duration-200">
                    {item.title}
                  </h3>
                  <p className="text-book-brown-dark mb-4 leading-relaxed">
                    {item.excerpt}
                  </p>
                  <div className="flex items-center text-book-brown-light group-hover:text-book-brown transition-colors duration-200">
                    <span className="font-medium">Read Full Article</span>
                    <ExternalLink className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          

        </div>
      </main>
      
    </div>
  );
};

export default News;
